import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { fetchContent } from "../../api/content";
import {
  completePackage,
  fetchPackage,
  saveRateCard,
  saveSelfCheck,
  uploadCv,
  verifyPortfolio,
  type PackageView,
  type RateCardInput,
} from "../../api/package";
import {
  completePosition,
  fetchAuditQuestions,
  fetchPlatforms,
  fetchPosition,
  markPlatformVisited,
  reviewNiche,
  saveNiche,
  submitAudit,
  verifyProfile,
  type PositionView,
} from "../../api/position";
import type { CheckItem } from "../../api/types";
import { Banner } from "../../components/Banner";
import { ScreenState } from "../../components/ScreenState";
import { attempt, messageOf } from "../../lib/errors";
import { registerDraftSender, saveOrQueue } from "../../lib/offlineSync";
import { useSession } from "../../lib/session";
import { useAsync } from "../../lib/useAsync";
import { useDebouncedCallback } from "../../lib/useDebounced";
import { PackageScreen } from "../../screens/package/PackageScreen";
import { PositionScreen } from "../../screens/position/PositionScreen";
import { DoneScreen } from "./DoneScreen";

registerDraftSender<string>("niche", saveNiche);
registerDraftSender<RateCardInput>("rateCard", saveRateCard);

export function PositionRoute() {
  const navigate = useNavigate();
  const loaded = useAsync(
    () => Promise.all([fetchPosition(), fetchPlatforms(), fetchAuditQuestions()]),
    [],
  );
  const [view, setView] = useState<PositionView | null>(null);
  const [verifyResults, setVerifyResults] = useState<
    Record<string, { checks: CheckItem[]; keptPrevious: boolean }>
  >({});
  const [busy, setBusy] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [finished, setFinished] = useState(false);

  useEffect(() => {
    if (loaded.data) setView(loaded.data[0]);
  }, [loaded.data]);

  const saveNicheSoon = useDebouncedCallback((niche: string) => {
    void saveOrQueue("niche", "niche", niche, async (value) =>
      setView(await saveNiche(value)),
    ).catch((e: unknown) => setError(messageOf(e)));
  });

  if (finished) {
    return (
      <DoneScreen
        kicker="Step 1 complete"
        title="You're live, findable and clear about what you offer."
        sub="Clients can now find you. Next you make sure that when they land on you, your work does the talking: a strong portfolio, a CV and a price."
        next={{ to: "/gig/2", label: "Start Step 2" }}
      />
    );
  }

  return (
    <ScreenState loading={loaded.loading} error={loaded.error} onRetry={loaded.reload}>
      {view && loaded.data ? (
        <>
          {error ? <Banner tone="bad">{error}</Banner> : null}
          <PositionScreen
            view={view}
            platforms={loaded.data[1]}
            auditQuestions={loaded.data[2]}
            busy={busy}
            aiBusy={aiBusy}
            verifyResults={verifyResults}
            onNicheChange={saveNicheSoon}
            onReviewNiche={() =>
              void attempt(setAiBusy, setError, async () => setView(await reviewNiche()))
            }
            onSubmitAudit={(answers) =>
              void attempt(setAiBusy, setError, async () => {
                const audit = await submitAudit(answers);
                setView((current) => (current ? { ...current, audit } : current));
              })
            }
            onVisit={(platformId) => void markPlatformVisited(platformId).catch(() => undefined)}
            onVerify={(platformId, url) =>
              void attempt(setBusy, setError, async () => {
                const result = await verifyProfile(platformId, url);
                setVerifyResults((current) => ({ ...current, [platformId]: result }));
                setView(await fetchPosition());
              })
            }
            onComplete={() =>
              void attempt(setBusy, setError, async () => {
                const wasDone = view.done;
                setView(await completePosition());
                if (!wasDone) setFinished(true);
                else void navigate("/hub");
              })
            }
          />
        </>
      ) : null}
    </ScreenState>
  );
}

export function PackageRoute() {
  const { user } = useSession();
  const loaded = useAsync(() => Promise.all([fetchPackage(), fetchContent()]), []);
  const [view, setView] = useState<PackageView | null>(null);
  const [verifyResult, setVerifyResult] = useState<{
    checks: CheckItem[];
    keptPrevious: boolean;
  } | null>(null);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [finished, setFinished] = useState(false);

  useEffect(() => {
    if (loaded.data) setView(loaded.data[0]);
  }, [loaded.data]);

  const saveRateSoon = useDebouncedCallback((input: RateCardInput) => {
    void saveOrQueue("rateCard", "rateCard", input, async (value) =>
      setView(await saveRateCard(value)),
    ).catch((e: unknown) => setError(messageOf(e)));
  });

  if (finished) {
    return (
      <DoneScreen
        kicker="Step 2 complete"
        title="A client can now open your work, see your price and read your CV."
        sub="Next is the peak of the pathway: the bidding simulator. You'll practice winning proposals on realistic jobs before any money is on the line."
        next={{ to: "/gig/3", label: "Start Step 3" }}
      />
    );
  }

  const track = user?.track;
  return (
    <ScreenState loading={loaded.loading} error={loaded.error} onRetry={loaded.reload}>
      {view && loaded.data && track ? (
        <>
          {error ? <Banner tone="bad">{error}</Banner> : null}
          <PackageScreen
            view={view}
            track={loaded.data[1].tracks[track]}
            exemplar={loaded.data[1].exemplars[track]}
            checklist={loaded.data[1].checklist}
            cvBuilders={loaded.data[1].cvBuilders}
            verifyResult={verifyResult}
            uploading={uploading}
            uploadError={uploadError}
            busy={busy}
            onVerifyPortfolio={(url) =>
              void attempt(setBusy, setError, async () => {
                const result = await verifyPortfolio(url);
                setVerifyResult(result);
                setView(result.view);
              })
            }
            onToggleSelf={(keys) => {
              setView({ ...view, step: { ...view.step, selfCheck: keys }, selfCount: keys.length });
              void attempt(
                () => undefined,
                setError,
                async () => setView(await saveSelfCheck(keys)),
              );
            }}
            onRateChange={(target, hours, packages) => saveRateSoon({ target, hours, packages })}
            onUploadCv={(file) =>
              void attempt(setUploading, setUploadError, async () => setView(await uploadCv(file)))
            }
            onComplete={() =>
              void attempt(setBusy, setError, async () => {
                const wasDone = view.done;
                setView(await completePackage());
                if (!wasDone) setFinished(true);
              })
            }
          />
        </>
      ) : null}
    </ScreenState>
  );
}
